import React from "react";
import PageHero from "../components/PageHero";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Stats from "../components/Stats";
import ContactCTA from "../components/ContactCTA";
import { assets, values, awards, company } from "../data/mock";
import { ShieldCheck, Hammer, Handshake, Leaf, Award } from "lucide-react";
import Team from "../components/Team";

const iconMap = { ShieldCheck, Hammer, Handshake, Leaf };

export default function About() {
  return (
    <div className="relative">
      <Header />
      <PageHero
        eyebrow="About R&C"
        title="Built on bridges,"
        accent="trusted for generations."
        subtitle="From a small Birmingham contractor in 1957 to ICE Innovation Award winners, six decades of building the structures Britain relies on."
        image={assets.team}
        crumbs={[{ label: "About" }]}
      />

      {/* Story */}
      <section className="py-24 lg:py-32 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5 reveal">
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Our Story</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05]">
                A heritage of <span className="text-amber-accent">building Britain.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-white/75 leading-relaxed reveal">
              <p>
                R&amp;C was founded in {company.established} as a family-run civil
                engineering contractor in Birmingham. Six decades later, we're based at the
                iconic depot beneath Spaghetti Junction, and our work has spread from local
                bridges to landmark national infrastructure.
              </p>
              <p>
                Three generations of craftsmen, engineers and project leaders have
                contributed to the structures, roads and railways our communities rely on
                every day. Whether it's a £500 emergency repair or an £8M scheme,
                we bring the same craft, the same safety standards and the same commitment.
              </p>
              <p>
                In 2012 we were honoured with the ICE Innovation of the Year Award. In 2023,
                we delivered a 120-year sustainable footbridge made from 26,000 recycled
                plastic bottles in the Peak District National Park. The next chapter? Greener,
                smarter, safer infrastructure for the Midlands and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats + Timeline */}
      <Stats />

      {/* Values */}
      <section className="py-24 lg:py-32 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14 reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Our Values</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              How we work, <span className="text-amber-accent">why we last.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => {
              const Icon = iconMap[v.icon] || ShieldCheck;
              return (
                <div
                  key={v.title}
                  className="reveal p-7 rounded-2xl bg-[#101014] border border-white/8 hover:border-[#F5D000]/40 transition-all group"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#F5D000]/10 border border-[#F5D000]/30 flex items-center justify-center mb-5 group-hover:bg-[#F5D000] transition-colors">
                    <Icon className="w-5 h-5 text-[#F5D000] group-hover:text-black" />
                  </div>
                  <h3 className="font-display text-xl font-bold mb-2 text-white">{v.title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <Team />

      {/* Awards */}
      <section className="py-24 lg:py-32 bg-[#08080a]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14 reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Awards & Memberships</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              Recognised for <span className="text-amber-accent">the work.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {awards.map((a, i) => (
              <div
                key={a.title}
                className="reveal p-7 rounded-2xl border border-white/8 bg-[#101014] hover:border-[#F5D000]/40 transition-all"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <Award className="w-7 h-7 text-[#F5D000] mb-4" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">{a.year}</div>
                <div className="font-display text-lg font-bold text-white mb-1">{a.title}</div>
                <div className="text-sm text-white/55">{a.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
      <Footer />
    </div>
  );
}
